import React, { useState } from 'react';
import { Icons } from './Icons';
import { Server, FirewallType, UserRole } from '../types';

interface DashboardProps {
  servers: Server[];
  onSelectServer: (server: Server) => void;
  onAddServer: (server: Omit<Server, 'id' | 'status' | 'rules' | 'resources' | 'services'>) => void;
  onLogout: () => void;
  onViewDatabase: () => void;
  onViewUsers: () => void;
  userRole: UserRole;
  targetUserId: string | null;
  onClearFilter: () => void;
  onDeleteServer: (serverId: string) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ servers, onSelectServer, onAddServer, userRole, targetUserId, onClearFilter, onDeleteServer }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [detectionStatus, setDetectionStatus] = useState<string>('');
  const [error, setError] = useState('');
  
  const [newServer, setNewServer] = useState({ 
    name: '', ip: '', port: 22, username: 'root', password: '',
    os: 'Unknown', firewallType: 'unknown' as FirewallType 
  });

  const handleConnectAndDetect = async (e: React.FormEvent) => {
    e.preventDefault();
    setDetecting(true);
    setDetectionStatus('Sunucuya bağlanılıyor (SSH)...');
    setError('');
    
    const auth_user_id = localStorage.getItem('user_id');

    try {
      const response = await fetch('/api/backend.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'detect_server',
          ip: newServer.ip,
          port: newServer.port,
          username: newServer.username,
          password: newServer.password,
          auth_user_id: auth_user_id 
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setDetectionStatus('Analiz tamamlandı. Sunucu ekleniyor...');
        onAddServer({
            name: newServer.name,
            ip: newServer.ip,
            port: newServer.port,
            username: newServer.username,
            password: newServer.password,
            os: data.detected_os || 'Linux Generic',
            firewallType: data.detected_firewall || 'iptables',
            detectedPanel: data.detected_panel
        });
        setShowAddModal(false);
        setNewServer({ name: '', ip: '', port: 22, username: 'root', password: '', os: 'Unknown', firewallType: 'unknown' });
      } else {
          setError('Hata: ' + data.message);
      }
    } catch (error) {
       setError('Sunucu API hatası (Backend erişilemez).');
    } finally {
        setDetecting(false);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, serverId: string) => {
      e.stopPropagation(); // Karta tıklanmasını engelle
      if(window.confirm('Bu sunucuyu ve tüm firewall kurallarını kalıcı olarak silmek istediğinize emin misiniz?')) {
          onDeleteServer(serverId);
      }
  };

  return (
    <div className="flex-1 bg-gray-950">
        <header className="h-20 border-b border-gray-800 flex items-center justify-between px-8 bg-gray-900/50 backdrop-blur-sm sticky top-0 z-20">
          <div className="flex items-center gap-4">
              <h1 className="text-2xl font-semibold">Sunucu Yönetimi</h1>
              {targetUserId && (
                  <div className="flex items-center gap-2 bg-yellow-900/30 text-yellow-400 px-3 py-1 rounded-full text-sm border border-yellow-800">
                      <span>Kullanıcı ID: {targetUserId} Görüntüleniyor</span>
                      <button onClick={onClearFilter} className="hover:text-white"><Icons.XCircle className="w-4 h-4"/></button>
                  </div>
              )}
          </div>
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-all shadow-lg shadow-blue-900/20">
            <Icons.Plus className="w-5 h-5" /> <span>Yeni Sunucu Ekle</span>
          </button>
        </header>

        <div className="p-8">
          {servers.length === 0 ? (
            <div className="text-center py-20">
               <div className="inline-flex p-6 bg-gray-900 rounded-full mb-4"><Icons.Server className="w-12 h-12 text-gray-700" /></div>
               <h3 className="text-xl font-bold text-gray-300">Henüz sunucu eklenmemiş</h3>
               <p className="text-gray-500 mt-2">Yönetime başlamak için yeni bir sunucu ekleyin.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {servers.map((server) => (
                <div key={server.id} onClick={() => onSelectServer(server)} className="group bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10 transition-all cursor-pointer relative overflow-hidden">
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-3 bg-gray-800 rounded-lg group-hover:bg-blue-900/20 transition-colors">
                      {server.firewallType === 'windows' ? <Icons.Monitor className="w-6 h-6 text-gray-400 group-hover:text-blue-400" /> : <Icons.Server className="w-6 h-6 text-gray-400 group-hover:text-blue-400" />}
                    </div>
                    
                    <div className="flex items-center gap-2">
                        {server.status === 'online' ? (
                            <div className="flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border bg-green-900/30 border-green-800 text-green-400">
                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div> ONLINE
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border bg-red-900/30 border-red-800 text-red-400">
                                <div className="w-2 h-2 rounded-full bg-red-500"></div> OFFLINE
                            </div>
                        )}
                        
                        <button onClick={(e) => handleDeleteClick(e, server.id)} className="p-2 text-gray-500 hover:bg-red-900/20 hover:text-red-400 rounded-lg transition-colors z-10" title="Sunucuyu Sil">
                            <Icons.Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">{server.name}</h3>
                  <div className="flex items-center gap-2 mb-4">
                     <span className="text-gray-400 text-sm font-mono">{server.ip}</span>
                     {server.detectedPanel && server.detectedPanel !== 'None' && (
                        <span className="text-xs bg-purple-900/30 text-purple-400 px-2 py-0.5 rounded border border-purple-800/50">{server.detectedPanel}</span>
                     )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-800">
                    <div><span className="text-xs text-gray-500 block mb-1">İşletim Sistemi</span><span className="text-sm font-medium text-gray-300 truncate block" title={server.os}>{server.os}</span></div>
                    <div><span className="text-xs text-gray-500 block mb-1">Firewall</span><span className="text-sm font-medium text-blue-400 font-mono uppercase">{server.firewallType}</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-900 rounded-2xl border border-gray-800 shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="p-6 border-b border-gray-800 flex justify-between items-center bg-gray-900/50">
              <h3 className="text-xl font-bold">Yeni Sunucu Bağla</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white" disabled={detecting}><Icons.XCircle className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleConnectAndDetect} className="p-6 space-y-4">
              {detecting ? (
                 <div className="flex flex-col items-center justify-center py-8 space-y-4">
                    <div className="relative">
                       <div className="w-16 h-16 border-4 border-gray-700 border-t-blue-500 rounded-full animate-spin"></div>
                       <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-b-purple-500 rounded-full animate-spin direction-reverse"></div>
                    </div>
                    <p className="text-blue-400 animate-pulse font-mono text-sm">{detectionStatus}</p>
                 </div>
              ) : (
                <>
                  {error && <div className="p-3 bg-red-900/20 border border-red-800 text-red-400 text-sm rounded mb-4">{error}</div>}
                  <div><label className="block text-sm font-medium text-gray-400 mb-2">Sunucu Adı</label><input required className="w-full bg-gray-950 border border-gray-800 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="Örn: Web Sunucum" value={newServer.name} onChange={e => setNewServer({...newServer, name: e.target.value})} /></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2"><label className="block text-sm font-medium text-gray-400 mb-2">IP Adresi</label><input required className="w-full bg-gray-950 border border-gray-800 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono" value={newServer.ip} onChange={e => setNewServer({...newServer, ip: e.target.value})} /></div>
                    <div><label className="block text-sm font-medium text-gray-400 mb-2">Port</label><input required type="number" className="w-full bg-gray-950 border border-gray-800 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono" value={newServer.port} onChange={e => setNewServer({...newServer, port: parseInt(e.target.value)})} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-gray-400 mb-2">Kullanıcı</label><input required className="w-full bg-gray-950 border border-gray-800 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" value={newServer.username} onChange={e => setNewServer({...newServer, username: e.target.value})} /></div>
                    <div><label className="block text-sm font-medium text-gray-400 mb-2">Şifre</label><input required type="password" className="w-full bg-gray-950 border border-gray-800 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" value={newServer.password} onChange={e => setNewServer({...newServer, password: e.target.value})} /></div>
                  </div>
                  <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg mt-4 transition-all">Bağlan ve Ekle</button>
                </>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};